// Claim lifecycle timestamps — one formatting contract shared by the
// dashboard, the Case Overview and the report metadata block.

export interface ClaimTimestamps {
  /** When the customer submitted the claim. */
  submittedAt: string | null;
  /** When payment was received (processing gate cleared). */
  paymentReceivedAt: string | null;
  /** When the analysis pipeline started its first job. */
  processingStartedAt: string | null;
  /** When the professional report became available. */
  reportCompletedAt: string | null;
  /** Last change of any kind on the claim record. */
  lastUpdatedAt: string | null;
}

export const EMPTY_TIMESTAMPS: ClaimTimestamps = {
  submittedAt: null,
  paymentReceivedAt: null,
  processingStartedAt: null,
  reportCompletedAt: null,
  lastUpdatedAt: null,
};

/** "Aug 2, 2026 2:54 PM" — the canonical claim timestamp rendering. */
export function formatClaimTimestamp(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "Aug 2, 2026 • 2:54 PM" — executive presentation of a claim timestamp. */
export function formatClaimStamp(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${date} • ${time}`;
}

/** Date-only rendering for contexts that must stay compact. */
export function formatClaimDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** Human elapsed duration between two instants, e.g. "40 minutes", "2 hr 15 min". */
export function formatElapsed(from?: string | null, to?: string | null): string | null {
  if (!from || !to) return null;
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  const mins = Math.round((b - a) / 60000);
  if (mins < 1) return "under a minute";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"}`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem ? `${hrs} hr ${rem} min` : `${hrs} hour${hrs === 1 ? "" : "s"}`;
  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;
  return remHrs ? `${days} d ${remHrs} hr` : `${days} day${days === 1 ? "" : "s"}`;
}

/** Total processing time for a claim, when both ends are known. */
export function processingElapsed(t: ClaimTimestamps): string | null {
  const start = t.processingStartedAt ?? t.paymentReceivedAt ?? t.submittedAt;
  return formatElapsed(start, t.reportCompletedAt);
}
